import { Mutation, MutationTree } from "vuex";
import State from "./state";
import moment from "moment";
import { User } from "../auth/types";
import { Environment } from "@/common/types";
import {
  JobDetail,
  ExamDetail,
  PartsDetail,
  Part,
  Answer
} from "@/common/interfaces";
import { ToggleQuestion } from "./actions";

export class MutationNames {
  public static Initialise: string = "initialise";
  public static SetUser: string = "setUser";
  // Set up Details
  public static SetJobId: string = "setJobId";
  public static SetJobDetail: string = "setJobDetail";
  public static SetExamDetail: string = "setExamDetail";
  public static SetPartsDetail: string = "setPartsDetail";
  public static SetChecklistQA: string = "setChecklistQA";
  public static SetChecklistQARow: string = "setChecklistQARow";
  // Job Detail mutations
  public static SetBookedInDateTime: string = "setBookedInDateTime";
  public static SetRoteIncluded: string = "setRoteIncluded";
  public static SetNextRoteDue: string = "setNextRoteDue";
  // Exam Detail mutations
  public static SetProofLoad: string = "setProofLoad";
  public static SetRadius: string = "setRadius";
  public static SetDefectDetails: string = "setDefectDetails";
  public static SetDefectRemedy: string = "setDefectRemedy";
  public static SetDefectWork: string = "setDefectWork";
  public static SetInaccessibleParts: string = "setInaccessibleParts";
  public static SetVisitNotes: string = "setVisitNotes";
  public static SetLiftSafe: string = "setLiftSafe";
  public static SetSafeWorkingLoad: string = "setSafeWorkingLoad";
  public static SetArrivedOnSite: string = "setArrivedOnSite";
  public static SetRoteToggleQuestion: string = "setRoteToggleQuestion";
  public static SetAdditionalParticulars: string = "setAdditionalParticulars";
  // Part mutations
  public static SetQuoteId: string = "setQuoteId";
  public static RemovePartFromQuote: string = "removePartFromQuote";
  public static AddPartToQuote: string = "addPartToQuote";
  public static AddPartToQuoteAtIndex: string = "addPartToQuoteAtIndex";
  // Answer mutations
  public static AddAnswers: string = "addAnswers";
  public static SetAnswers: string = "setAnswers";
}

function initialise(state: State, e: Environment) {
  state.Environment = e;
}

function setUser(state: State, u: User) {
  state.User = u;
}

function setJobId(state: State, jobId: number) {
  state.Job.JobId = jobId;
}

function setJobDetail(state: State, jobDetail: JobDetail) {
  state.Job.JobDetail = jobDetail;
}

function setExamDetail(state: State, examDetail: ExamDetail) {
  state.Job.ExamDetail = examDetail;
}

function setPartsDetail(state: State, partsDetail: PartsDetail) {
  state.Job.PartsDetail = partsDetail;
}

function setChecklistQA(state: State, checklistQA: { [qk: string]: string }) {
  state.Job.ChecklistQAs = checklistQA;
}

function setChecklistQARow(
  state: State,
  payload: { questionKey: string; answerKey: string }
) {
  if (state.Job.ChecklistQAs) {
    state.Job.ChecklistQAs[payload.questionKey] = payload.answerKey;
  }
}

function setBookedInDateTime(state: State, bookedInDateTime: moment.Moment) {
  state.Job.JobDetail!.BookedInDateTime = bookedInDateTime;
}

function setRoteIncluded(state: State, newValue: boolean | null) {
  state.Job.JobDetail!.ROTEIncluded = newValue;
}

function setNextRoteDue(state: State, newValue: moment.Moment) {
  state.Job.JobDetail!.NextRoteDue = newValue;
}

function setProofLoad(state: State, proofLoad: string) {
  state.Job.ExamDetail!.ProofLoad = proofLoad;
}

function setRadius(state: State, radius: string) {
  state.Job.ExamDetail!.Radius = radius;
}

function setArrivedOnSite(state: State, arrivedOnSite: moment.Moment) {
  state.Job.ExamDetail!.ArrivedOnSite = arrivedOnSite;
}

function setLiftSafe(state: State, liftSafe: boolean) {
  state.Job.ExamDetail!.LiftSafe = liftSafe;
}

function setDefectDetails(state: State, defectDetails: string) {
  state.Job.ExamDetail!.DefectDetails = defectDetails;
}

function setDefectRemedy(state: State, defectRemedy: string) {
  state.Job.ExamDetail!.DefectRemedy = defectRemedy;
}

function setDefectWork(state: State, defectWork: string) {
  state.Job.ExamDetail!.DefectWork = defectWork;
}

function setRoteToggleQuestion(
  state: State,
  payLoad: { question: ToggleQuestion; newValue: boolean | null }
) {
  (state.Job.ExamDetail as any)[payLoad.question.storeFieldName!] =
    payLoad.newValue;
}

function setAdditionalParticulars(state: State, additionalParticulars: string) {
  state.Job.ExamDetail!.AddParticulars = additionalParticulars;
}

function setInaccessibleParts(state: State, inaccessibleParts: string) {
  state.Job.ExamDetail!.InaccessibleParts = inaccessibleParts;
}

function setVisitNotes(state: State, visitNotes: string) {
  state.Job.ExamDetail!.VisitNotes = visitNotes;
}

function setSafeWorkingLoad(state: State, Swl: string) {
  state.Job.JobDetail!.Swl = Swl;
}

function setQuoteId(state: State, quoteId: number) {
  state.Job.PartsDetail!.QuoteId = quoteId;
}

function removePartFromQuote(state: State, partIndex: number) {
  state.Job.PartsDetail!.Parts.splice(partIndex, 1);
}

function addPartToQuote(state: State, part: Part) {
  state.Job.PartsDetail!.Parts.push(part);
}

function addPartToQuoteAtIndex(
  state: State,
  payload: { partIndex: number; part: Part }
) {
  state.Job.PartsDetail!.Parts.splice(payload.partIndex, 0, payload.part);
}
function setAnswers(state: State, answers: Answer[]) {
  state.Answers = answers;
}

function addAnswers(state: State, answers: Answer[]) {
  state.Answers.concat(answers);
}

const Mutations: MutationTree<State> = {
  initialise,
  setUser,
  setJobId,
  setJobDetail,
  setExamDetail,
  setPartsDetail,
  setChecklistQA,
  setChecklistQARow,
  setBookedInDateTime,
  setProofLoad,
  setRadius,
  setArrivedOnSite,
  setDefectDetails,
  setDefectRemedy,
  setDefectWork,
  setRoteToggleQuestion,
  setAdditionalParticulars,
  setInaccessibleParts,
  setVisitNotes,
  setLiftSafe,
  setSafeWorkingLoad,
  setQuoteId,
  removePartFromQuote,
  addPartToQuote,
  addPartToQuoteAtIndex,
  setAnswers,
  addAnswers,
  setRoteIncluded,
  setNextRoteDue
};

export default Mutations;
